// src/services/ApiService.js
/**
 * Service API pour la communication avec le backend
 * Gère également les cas de mode hors ligne et l'authentification
 */


// Configuration dynamique de l'URL de base
const API_BASE_URL = (() => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  
  // NOUVEAU DOMAINE PRINCIPAL - Production bccv.online
  if (hostname === 'bccv.online' || 
      hostname === 'www.bccv.online') {
    return `${protocol}//bccv.online/api`;
  }
  
  // Production (ancien domaine .com)
  if (hostname === 'boissonsducameroun-chezvous.com' || 
      hostname === 'www.boissonsducameroun-chezvous.com') {
    return `${protocol}//boissonsducameroun-chezvous.com/api`;
  }
  
  // Ancien domaine XYZ (période de transition)
  if (hostname === 'boissonsducameroun-chezvous.xyz' || 
      hostname === 'www.boissonsducameroun-chezvous.xyz') {
    return `${protocol}//boissonsducameroun-chezvous.xyz/api`;
  }
  
  // Développement local
  return 'http://localhost/boissons-cameroun/api';
})();

console.log('API URL configurée:', API_BASE_URL);

class ApiService {
  
  /**
   * Obtenir l'URL de base de l'API
   * @returns {string} - L'URL de base
   */
  static getBaseUrl() {
    return API_BASE_URL;
  }
  
  /**
   * Méthode auxiliaire pour gérer les erreurs
   * @param {Response} response - La réponse de fetch à traiter
   * @returns {Promise<any>} - Les données JSON de la réponse
   * @throws {Error} - Une erreur si la réponse n'est pas OK
   */
  static async handleResponse(response) {
  try {
    const clonedResponse = response.clone();
    const data = await clonedResponse.json();
    
    console.debug('API Response:', {
      url: response.url,
      status: response.status,
      ok: response.ok,
      data
    });

    // Si la réponse contient un champ 'success', on la retourne directement
    if (typeof data.success !== 'undefined') {
      return data;
    }
    
    if (response.ok) {
      return data;
    }
    
    throw new Error(data.message || `Erreur HTTP ${response.status}`);
    
  } catch (e) {
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `Erreur HTTP ${response.status}`);
    }
    
    console.warn('Impossible de parser la réponse en JSON:', e);
    return { message: 'Réponse non-JSON reçue du serveur' };
  }
}

  /**
   * Méthode auxiliaire pour gérer les erreurs hors ligne
   * @returns {never} - Lance toujours une erreur
   * @throws {Error} - Une erreur indiquant que l'utilisateur est hors ligne
   */
  static handleOfflineError() {
    console.log('Mode hors ligne détecté, utilisation des données en cache');
    throw new Error('Vous êtes hors ligne');
  }

  /**
   * Méthode pour effectuer les requêtes API avec gestion des erreurs et authentification
   * @param {string} endpoint - Le point de terminaison API (sans l'URL de base)
   * @param {Object} options - Les options de la requête fetch
   * @returns {Promise<any>} - Les données JSON de la réponse
   */
  static async fetchWithErrorHandling(endpoint, options = {}) {
    if (!navigator.onLine) {
      return this.handleOfflineError();
    }
    
    // Récupérer le token d'authentification
    const token = localStorage.getItem('authToken');
	console.log('📤 Token envoyé:', token ? 'Oui' : 'Non', token);
    
    // S'assurer que les options par défaut sont définies
    const defaultOptions = {
	  headers: {
		'Accept': 'application/json',
		'Content-Type': 'application/json',
		'X-Authorization': `Bearer ${token}`,           // ← Prioritaire
		...(token && { 'Authorization': `Bearer ${token}` }) // ← Pour compatibilité
	  },
	  mode: 'cors'
	};
    
    const fetchOptions = { ...defaultOptions, ...options };
    
    // Fusionner les headers si des headers supplémentaires sont fournis
    if (options.headers) {
      fetchOptions.headers = { ...defaultOptions.headers, ...options.headers };
    }
    
    try {
      // Définir un timeout pour les requêtes
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 secondes
      
      fetchOptions.signal = controller.signal;
      
      // Log pour le débogage
      console.log(`Fetching: ${API_BASE_URL}${endpoint}`, fetchOptions);
      
      // Utiliser un mécanisme de retry pour les requêtes qui échouent
      let attempts = 0;
      const maxAttempts = 3;
      let lastError = null;
      
      while (attempts < maxAttempts) {
        try {
          const response = await fetch(`${API_BASE_URL}${endpoint}`, fetchOptions);
          
          // Annuler le timeout
          clearTimeout(timeoutId);
          
          // Si 401 Unauthorized, nettoyer l'auth et rediriger
          if (response.status === 401 && endpoint !== '/auth/?action=check' && endpoint !== '/auth/?action=verify-otp') {
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
            window.location.href = '/register';
            throw new Error('Session expirée');
          }
          
          return await this.handleResponse(response);
        } catch (error) {
          attempts++;
          lastError = error;
          
          if (error.name === 'AbortError') {
            throw new Error('La requête a pris trop de temps à répondre');
          }
          
          console.warn(`Tentative ${attempts}/${maxAttempts} échouée:`, error);
          
          // Si ce n'est pas la dernière tentative, attendre avant de réessayer
          if (attempts < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, 1000 * attempts)); // Attente progressive
          }
        }
      }
      
      // Si toutes les tentatives ont échoué
      console.error(`Échec après ${maxAttempts} tentatives pour ${endpoint}:`, lastError);
      throw lastError;
    } catch (error) {
      console.error(`Erreur lors de la requête vers ${endpoint}:`, error);
      throw error;
    }
  }

  /**
   * Récupérer tous les distributeurs
   * @returns {Promise<Array>} - Liste des distributeurs
   */
  static async fetchAllDistributeurs() {
    return this.fetchWithErrorHandling('/distributeurs/');
  }

  /**
   * Récupérer un distributeur spécifique
   * @param {number} id - ID du distributeur
   * @returns {Promise<Object>} - Détails du distributeur
   */
  static async fetchDistributeur(id) {
    return this.fetchWithErrorHandling(`/distributeurs/${id}`);
  }

  /**
   * Récupérer un distributeur par défaut (pour le mode hors ligne)
   * @returns {Promise<Object>} - Distributeur par défaut
   */
  static async fetchDefaultDistributor() {
    if (!navigator.onLine) {
      // En mode hors ligne, créer un distributeur par défaut
      return {
        id: 1,
        nom: "JM Distribution SARL",
        adresse: "Bonamoussadi Sable",
        telephone: "671234567",
        email: "contact@distributeur.com",
        latitude: 4.08313, // Coordonnées de Bonamoussadi (Douala)
        longitude: 9.72586,
        distance: 0,
        isDefault: true
      };
    }
    
    // En mode en ligne, récupérer le premier distributeur
    try {
      const distributeurs = await this.fetchAllDistributeurs();
      return distributeurs && distributeurs.length > 0 ? distributeurs[0] : {
        id: 1,
        nom: "Distributeur par défaut",
        adresse: "Adresse non disponible",
        latitude: 4.05,
        longitude: 9.73,
        distance: 0,
        isDefault: true
      };
    } catch (error) {
      console.error('Erreur lors de la récupération du distributeur par défaut:', error);
      return {
        id: 1,
        nom: "Distributeur par défaut",
        adresse: "Adresse non disponible",
        latitude: 4.05,
        longitude: 9.73,
        distance: 0,
        isDefault: true
      };
    }
  }

  /**
   * Récupérer les produits d'un distributeur
   * @param {number} id - ID du distributeur
   * @param {boolean} forceRefresh - Forcer un rafraîchissement des données
   * @returns {Promise<Array>} - Liste des produits
   */
  static async fetchProduitsDistributeur(id, forceRefresh = true) {
    const endpoint = `/distributeurs/${id}/produits`;
    
    const options = {};
    
    if (forceRefresh) {
      options.headers = {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      };
    }
    
    return this.fetchWithErrorHandling(endpoint, options);
  }

  /**
   * Créer une commande
   * @param {Object} orderData - Données de la commande
   * @returns {Promise<Object>} - Commande créée
   */
  static async createOrder(orderData) {
    console.log("ApiService.createOrder appelé avec:", orderData);
    
    // Solution temporaire : appeler directement index.php pour éviter les redirections
    const endpoint = '/commandes/index.php';
    
    console.log("Envoi POST vers:", `${API_BASE_URL}${endpoint}`);
    
    const response = await this.fetchWithErrorHandling(endpoint, {
      method: 'POST',
      body: JSON.stringify(orderData)
    });

    // Log de la réponse complète pour le débogage
    console.log("Réponse API createOrder:", response);

    // CORRECTION : Gérer le cas où l'API retourne la liste au lieu de la commande créée
    if (response && response.commandes && Array.isArray(response.commandes)) {
      console.warn("L'API a retourné une liste de commandes au lieu de la commande créée");
      
      // Supposons que la dernière commande de la liste est celle qu'on vient de créer
      // (car la liste est triée par date_creation DESC)
      const derniereCommande = response.commandes[0];
      
      if (derniereCommande && derniereCommande.id_commande) {
        console.log("Utilisation de la dernière commande de la liste:", derniereCommande);
        
        // Vérifier que c'est bien notre commande en comparant les données
        // (optionnel mais plus sûr)
        try {
          const orderVerification = await this.fetchOrderById(derniereCommande.id_commande);
          if (orderVerification && orderVerification.id_commande) {
            console.log("Commande vérifiée avec succès:", orderVerification.id_commande);
            return orderVerification;
          }
        } catch (error) {
          console.warn("Erreur lors de la vérification, utilisation de la commande de la liste");
          return derniereCommande;
        }
      }
    }

    // Comportement normal si l'API retourne directement la commande créée
    if (response) {
      // Si response.id existe, mais pas response.id_commande
      if (response.id && !response.id_commande) {
        response.id_commande = response.id;
        console.log("ID harmonisé: response.id -> response.id_commande", response.id_commande);
      }
      // Si response.id_commande existe, mais pas response.id
      else if (response.id_commande && !response.id) {
        response.id = response.id_commande;
        console.log("ID harmonisé: response.id_commande -> response.id", response.id);
      }
      // Si aucun des deux n'existe, chercher un autre champ qui pourrait contenir l'ID
      else if (!response.id && !response.id_commande) {
        // Essayer de trouver un champ contenant "id" dans son nom
        for (const key in response) {
          if (key.toLowerCase().includes('id') && typeof response[key] === 'number') {
            response.id_commande = response[key];
            response.id = response[key];
            console.log(`ID trouvé dans le champ ${key}:`, response[key]);
            break;
          }
        }
      }

      // Si on a trouvé un id_commande dans la réponse, vérifier avec fetchOrderById pour confirmer
      if (response.id_commande) {
        try {
          console.log("Vérification de l'existence de la commande avec ID:", response.id_commande);
          const orderVerification = await this.fetchOrderById(response.id_commande);
          
          // CORRECTION : Gérer le cas où orderVerification est un tableau
          if (orderVerification) {
            // Si c'est un tableau, chercher la commande avec le bon ID
            if (Array.isArray(orderVerification)) {
              console.warn("fetchOrderById a retourné un tableau au lieu d'un objet");
              // Chercher la commande avec l'ID correspondant
              const commande = orderVerification.find(c => c.id_commande === response.id_commande);
              if (commande) {
                console.log("Commande trouvée dans le tableau avec ID:", commande.id_commande);
                return commande;
              } else {
                console.error(`Commande ${response.id_commande} non trouvée dans le tableau retourné`);
                // Retourner la réponse originale si on ne trouve pas la commande
                return response;
              }
            } 
            // Si c'est un objet avec id_commande
            else if (orderVerification.id_commande) {
              // Vérifier que c'est bien la bonne commande
              if (orderVerification.id_commande === response.id_commande) {
                console.log("Commande vérifiée et confirmée avec ID:", orderVerification.id_commande);
                return orderVerification;
              } else {
                console.error(`ID de commande ne correspond pas: attendu ${response.id_commande}, reçu ${orderVerification.id_commande}`);
                return response;
              }
            }
            // Si c'est un objet avec une structure différente (ex: { commandes: [...] })
            else if (orderVerification.commandes && Array.isArray(orderVerification.commandes)) {
              const commande = orderVerification.commandes.find(c => c.id_commande === response.id_commande);
              if (commande) {
                console.log("Commande trouvée dans la liste avec ID:", commande.id_commande);
                return commande;
              } else {
                console.error(`Commande ${response.id_commande} non trouvée dans commandes[]`);
                return response;
              }
            }
          }
        } catch (verificationError) {
          console.warn(`Erreur lors de la vérification de la commande #${response.id_commande}:`, verificationError);
          // Continuer avec la réponse originale même si la vérification échoue
        }
      }
    }

    return response;
  }

  /**
   * Récupérer une commande par son ID
   * @param {number} id - ID de la commande
   * @returns {Promise<Object>} - Détails de la commande
   */
  static async fetchOrderById(id) {
    return this.fetchWithErrorHandling(`/commandes/${id}`);
  }

  /**
   * Mettre à jour le statut d'une commande
   * @param {number} orderId - ID de la commande
   * @param {string} status - Nouveau statut
   * @returns {Promise<Object>} - Résultat de la mise à jour
   */
  static async updateOrderStatus(orderId, status) {
    return this.fetchWithErrorHandling(`/commandes/${orderId}`, {
      method: 'PUT',
      body: JSON.stringify({ id_commande: orderId, statut: status })
    });
  }

  /**
   * S'abonner aux notifications d'une commande
   * @param {number} orderId - ID de la commande
   * @returns {Promise<boolean>} - Succès de l'abonnement
   */
  static async subscribeToOrderNotifications(orderId) {
    try {
      console.log("Tentative d'abonnement aux notifications pour la commande:", orderId);
      
      // Vérifier si les notifications sont prises en charge
      if (!('Notification' in window)) {
        console.warn('Les notifications ne sont pas prises en charge par ce navigateur');
        return false;
      }

      // Vérifier si la permission est accordée
      if (Notification.permission !== 'granted') {
        console.warn('Permission de notification non accordée');
        return false;
      }

      // Vérifier si le Service Worker est disponible
      if (!('serviceWorker' in navigator)) {
        console.warn('Service Worker non pris en charge par ce navigateur');
        
        // Même sans Service Worker, on peut stocker l'abonnement
        await this.storeOrderSubscription(orderId);
        return true;
      }

      try {
        // Obtenir l'enregistrement du Service Worker
        const registration = await navigator.serviceWorker.ready;
        console.log('Service Worker prêt:', registration);

        // Si nous avons PushManager, essayer de s'abonner
        if ('PushManager' in window) {
          try {
            // Vérifier l'abonnement push existant
            let subscription = await registration.pushManager.getSubscription();
            
            // S'il n'y a pas d'abonnement, essayer d'en créer un
            if (!subscription) {
              try {
                console.log('Tentative de création d\'un abonnement push...');
                
                // Clé VAPID factice - à remplacer par une vraie clé en production
                const vapidPublicKey = 'BLm3enLt4QMsaTwSa6JKMIhQC8f7qmrQn1gXT_a02yVO3xWYKcX0MlZlplLJ_50osBBYY5rn444QK4QNGm9NTBM';
                
                // Convertir la clé VAPID
                const applicationServerKey = this.urlBase64ToUint8Array(vapidPublicKey);
                
                // Essayer de s'abonner
                subscription = await registration.pushManager.subscribe({
                  userVisibleOnly: true,
                  applicationServerKey: applicationServerKey
                });
                
                console.log('Nouvel abonnement push créé:', subscription);
              } catch (pushError) {
                console.warn('Impossible de créer un abonnement push:', pushError);
                // Continuer quand même, on peut fonctionner sans push dans certains navigateurs
              }
            }
          } catch (pushManagerError) {
            console.warn('Erreur avec PushManager:', pushManagerError);
            // Continuer sans push notifications
          }
        }

        // Stocker l'abonnement dans IndexedDB, même si on n'a pas pu obtenir d'abonnement push
        await this.storeOrderSubscription(orderId);
        console.log('Abonnement stocké avec succès pour la commande:', orderId);
        
        return true;
      } catch (swError) {
        console.error('Erreur avec le Service Worker:', swError);
        
        // Dans certains cas, on peut quand même stocker l'abonnement même sans Service Worker
        await this.storeOrderSubscription(orderId);
        return true;
      }
    } catch (error) {
      console.error(`Erreur lors de l'abonnement aux notifications pour la commande #${orderId}:`, error);
      return false;
    }
  }

  /**
   * Convertit une chaîne base64 en tableau d'octets pour l'API PushManager
   * @param {string} base64String - La clé publique VAPID en base64
   * @returns {Uint8Array} - La clé convertie
   */
  static urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    
    return outputArray;
  }

  /**
   * Stocker l'abonnement à une commande dans IndexedDB
   * @param {number|string} orderId - ID de la commande
   * @returns {Promise<boolean>} - Succès du stockage
   */
  static async storeOrderSubscription(orderId) {
    return new Promise((resolve, reject) => {
      try {
        console.log(`Stockage de l'abonnement pour la commande ${orderId}...`);
        const openRequest = indexedDB.open('DrinkDeliveryApp', 3);
        
        openRequest.onupgradeneeded = (event) => {
          const db = event.target.result;
          
          // Créer l'object store 'orderSubscriptions' s'il n'existe pas
          if (!db.objectStoreNames.contains('orderSubscriptions')) {
            db.createObjectStore('orderSubscriptions', { keyPath: 'id' });
          }
        };
        
        openRequest.onsuccess = (event) => {
          try {
            const db = event.target.result;
            const transaction = db.transaction(['orderSubscriptions'], 'readwrite');
            const store = transaction.objectStore('orderSubscriptions');
            
            const subscription = {
              id: orderId.toString(),
              timestamp: new Date().getTime()
            };
            
            const request = store.put(subscription);
            
            request.onsuccess = () => {
              console.log(`Abonnement stocké avec succès dans IndexedDB pour la commande ${orderId}`);
              
              // Également stocker dans localStorage comme méthode de secours
              try {
                const existingSubscriptions = localStorage.getItem('orderSubscriptions');
                let subscriptions = [];
                
                if (existingSubscriptions) {
                  subscriptions = JSON.parse(existingSubscriptions);
                }
                
                // Vérifier si cette commande est déjà abonnée
                if (!subscriptions.some(sub => sub.id === orderId.toString())) {
                  subscriptions.push({
                    id: orderId.toString(),
                    timestamp: new Date().getTime()
                  });
                }
                
                localStorage.setItem('orderSubscriptions', JSON.stringify(subscriptions));
                console.log(`Abonnement également stocké dans localStorage`);
              } catch (localStorageError) {
                console.warn('Erreur lors du stockage dans localStorage:', localStorageError);
              }
              
              resolve(true);
            };
            
            request.onerror = (e) => {
              console.error("Erreur lors du stockage de l'abonnement:", e);
              resolve(false);
            };
          } catch (innerError) {
            console.error("Erreur lors de la transaction:", innerError);
            resolve(false);
          }
        };
        
        openRequest.onerror = (error) => {
          console.error("Erreur lors de l'ouverture de la base de données:", error);
          resolve(false);
        };
      } catch (outerError) {
        console.error("Erreur globale:", outerError);
        resolve(false);
      }
    });
  }

  /**
   * Vérifier si l'utilisateur est abonné aux notifications d'une commande
   * @param {number|string} orderId - ID de la commande
   * @returns {Promise<boolean>} - L'utilisateur est abonné
   */
  static async isSubscribedToOrder(orderId) {
    // Convertir orderId en chaîne pour la comparaison
    const orderIdStr = orderId.toString();
    
    // D'abord vérifier dans localStorage (plus rapide)
    try {
      const subscriptionsStr = localStorage.getItem('orderSubscriptions');
      if (subscriptionsStr) {
        const subscriptions = JSON.parse(subscriptionsStr);
        if (Array.isArray(subscriptions) && subscriptions.some(sub => sub.id === orderIdStr)) {
          console.log(`Abonnement trouvé dans localStorage pour la commande ${orderIdStr}`);
          return true;
        }
      }
    } catch (e) {
      console.warn('Erreur lors de la vérification dans localStorage:', e);
    }
    
    // Ensuite vérifier dans IndexedDB
    return new Promise((resolve) => {
      try {
        console.log(`Vérification de l'abonnement pour la commande ${orderIdStr} dans IndexedDB...`);
        const openRequest = indexedDB.open('DrinkDeliveryApp', 3);
        
        openRequest.onsuccess = (event) => {
          try {
            const db = event.target.result;
            
            if (!db.objectStoreNames.contains('orderSubscriptions')) {
              console.log('Object store "orderSubscriptions" non trouvé');
              resolve(false);
              return;
            }
            
            const transaction = db.transaction(['orderSubscriptions'], 'readonly');
            const store = transaction.objectStore('orderSubscriptions');
            const request = store.get(orderIdStr);
            
            request.onsuccess = () => {
              const isSubscribed = !!request.result;
              console.log(`Abonnement pour la commande ${orderIdStr} trouvé dans IndexedDB:`, isSubscribed);
              resolve(isSubscribed);
            };
            
            request.onerror = (e) => {
              console.error("Erreur lors de la récupération de l'abonnement:", e);
              resolve(false);
            };
          } catch (error) {
            console.error("Erreur lors de la vérification de l'abonnement:", error);
            resolve(false);
          }
        };
        
        openRequest.onerror = (error) => {
          console.error("Erreur lors de l'ouverture de la base de données:", error);
          resolve(false);
        };
      } catch (error) {
        console.error("Erreur globale:", error);
        resolve(false);
      }
    });
  }

  // ===== MÉTHODES D'AUTHENTIFICATION =====

  /**
   * Inscription d'un nouvel utilisateur
   */
  static async register(userData) {
    return this.fetchWithErrorHandling('/auth/?action=register', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
  }

  /**
   * Connexion - Demande de code OTP
   */
  static async login(credentials) {
    return this.fetchWithErrorHandling('/auth/?action=login', {
      method: 'POST',
      body: JSON.stringify(credentials)
    });
  }

  /**
   * Connexion par téléphone - Étape 1 (Demande OTP SMS)
   * @param {string} telephone - Numéro de téléphone
   * @returns {Promise<Object>} - Résultat de l'envoi
   */
  static async loginPhone(telephone) {
    return this.fetchWithErrorHandling('/auth/?action=login-phone', {
      method: 'POST',
      body: JSON.stringify({ telephone })
    });
  }

  /**
   * Vérifier si un numéro de téléphone existe
   * @param {string} telephone - Numéro de téléphone
   * @returns {Promise<Object>} - Statut d'existence
   */
  static async checkPhoneExists(telephone) {
    return this.fetchWithErrorHandling(`/auth/?action=check-phone&telephone=${telephone}`, {
      method: 'GET'
    });
  }

  /**
   * Vérification de l'email via token
   */
  static async verifyEmail(token) {
    return this.fetchWithErrorHandling('/auth/?action=verify-email', {
      method: 'POST',
      body: JSON.stringify({ token })
    });
  }

  /**
   * Vérifier si l'utilisateur est authentifié
   */
  static async checkAuth() {
    return this.fetchWithErrorHandling('/auth/?action=check', {
      method: 'GET'
    });
  }

  /**
   * Renvoyer l'email de vérification
   */
  static async resendVerificationEmail(email) {
    return this.fetchWithErrorHandling('/auth/?action=resend-verification', {
      method: 'POST',
      body: JSON.stringify({ email })
    });
  }
  
  /**
   * Mettre à jour le profil ou les préférences de l'utilisateur connecté
   * @param {Object} data - { action: 'update-profile'|'update-preferences', ...champs }
   * @returns {Promise<Object>} - { success, message, user }
   */
  static async updateProfile(data) {
    return this.fetchWithErrorHandling('/auth/update_profile.php', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Désactiver le compte de l'utilisateur (suppression douce - statut_compte = 0)
   */
  static async deleteAccount() {
    return await this.fetchWithErrorHandling('/auth/?action=delete-account', {
      method: 'POST'
    });
  }

  /**
   * Déconnexion
   */
  static async logout() {
    try {
      await this.fetchWithErrorHandling('/auth/?action=logout', {
        method: 'POST'
      });
    } catch (error) {
      // Continuer même en cas d'erreur
    } finally {
      // Nettoyer le localStorage
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      localStorage.removeItem('cart');
      localStorage.removeItem('deliveryLocation');
    }
  }

  /**
   * Obtenir l'utilisateur actuel depuis le localStorage
   */
  static getCurrentUser() {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch (e) {
        return null;
      }
    }
    return null;
  }

  /**
   * Vérifier si l'utilisateur est connecté
   */
  static isAuthenticated() {
    return !!localStorage.getItem('authToken');
  }
  
  // ===== MÉTHODES DE VÉRIFICATION DU TÉLÉPHONE =====

  /**
   * Envoyer un code de vérification SMS
   * @param {string} telephone - Numéro de téléphone
   * @param {number} orderId - ID de la commande (optionnel)
   * @returns {Promise<Object>} - Résultat de l'envoi
   */
  static async sendPhoneVerification(telephone, orderId = null) {
    return this.fetchWithErrorHandling('/sms/?action=send_verification', {
      method: 'POST',
      body: JSON.stringify({ telephone, orderId })
    });
  }
  
  /**
   * Vérifier le numéro de téléphone (envoyer code de vérification)
   * @param {string} telephone - Numéro de téléphone
   * @returns {Promise<Object>} - Résultat de l'envoi
   */
  static async verifyPhone(telephone) {
    return this.fetchWithErrorHandling('/auth/?action=verify-phone', {
      method: 'POST',
      body: JSON.stringify({ telephone })
    });
  }

  /**
   * Vérifier l'adresse email (envoyer code de vérification)
   * @param {string} email - Adresse email
   * @returns {Promise<Object>} - Résultat de l'envoi
   */
  static async verifyEmailAddress(email) {
    return this.fetchWithErrorHandling('/auth/?action=verify-email-address', {
      method: 'POST',
      body: JSON.stringify({ email })
    });
  }

  /**
   * Modifier la méthode verifyOTP existante pour gérer le type
   */
  static async verifyOTP(data) {
    const body = {
      action: 'verify-otp',
      code: data.code
    };
    
    // Ajouter le type si présent
    if (data.email) body.email = data.email;
    if (data.telephone) body.telephone = data.telephone;
    if (data.type) body.type = data.type; // pour vérification post-inscription
    
    return this.fetchWithErrorHandling('/auth/?action=verify-otp', {
      method: 'POST',
      body: JSON.stringify(body)
    });
  }

  /**
   * Vérifier un code SMS
   * @param {string} telephone - Numéro de téléphone
   * @param {string} code - Code de vérification
   * @param {number} orderId - ID de la commande (optionnel)
   * @returns {Promise<Object>} - Résultat de la vérification
   */
  static async verifyPhoneCode(telephone, code, orderId = null) {
    return this.fetchWithErrorHandling('/sms/?action=verify_code', {
      method: 'POST',
      body: JSON.stringify({ telephone, code, orderId })
    });
  }

  /**
   * Vérifier le statut de vérification d'un numéro
   * @param {string} telephone - Numéro de téléphone
   * @returns {Promise<Object>} - Statut de vérification
   */
  static async checkPhoneVerificationStatus(telephone) {
    return this.fetchWithErrorHandling(`/auth/?action=check-phone&telephone=${telephone}`, {
      method: 'GET'
    });
  }
  
  static async linkPaymentToOrder(data) {
	return this.fetchWithErrorHandling('/link-payment.php', {
	  method: 'POST',
	  body: JSON.stringify(data)
	});
  }
  
  /**
   * Envoyer un SMS de notification de commande au client
   * @param {number} orderId - ID de la commande
   * @returns {Promise<Object>} - Résultat de l'envoi
   */
  static async sendOrderNotificationSMS(orderId) {
    return this.fetchWithErrorHandling('/sms/?action=send_order_notification', {
      method: 'POST',
      body: JSON.stringify({ orderId })
    });
  }
  
 /**
 * Récupérer toutes les commandes de l'utilisateur connecté
 */
static async getUserOrders() {
  try {
    const token = localStorage.getItem('authToken');
    
    if (!token) {
      throw new Error('Token d\'authentification manquant');
    }

    const response = await fetch(`${API_BASE_URL}/commandes/`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      if (response.status === 401) {
        // Token invalide ou expiré
        this.clearAuth();
        throw new Error('Session expirée. Veuillez vous reconnecter.');
      }
      throw new Error(`Erreur HTTP: ${response.status}`);
    }

    const data = await response.json();
    
    // Trier les commandes par date (plus récentes en premier)
    if (Array.isArray(data)) {
      return data.sort((a, b) => {
        return new Date(b.date_commande) - new Date(a.date_commande);
      });
    }
    
    return data;
  } catch (error) {
    console.error('Erreur lors de la récupération des commandes:', error);
    throw error;
  }
}

/**
 * Récupérer une commande spécifique par son ID
 */
static async getOrderById(orderId) {
  try {
    const token = localStorage.getItem('authToken');
    
    if (!token) {
      throw new Error('Token d\'authentification manquant');
    }

    const response = await fetch(`${API_BASE_URL}/commandes/${orderId}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      if (response.status === 401) {
        this.clearAuth();
        throw new Error('Session expirée. Veuillez vous reconnecter.');
      }
      if (response.status === 404) {
        throw new Error('Commande introuvable');
      }
      throw new Error(`Erreur HTTP: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Erreur lors de la récupération de la commande:', error);
    throw error;
  }
}
}

export default ApiService;